import { toast } from '@/components/toast';
import { MAX_DURATION_SECONDS, MAX_FILE_SIZE_MB, MIN_DURATION_SECONDS, NEW_MAX_DURATION_SECONDS } from '@/pages/translation/constants';
import { getVideoDurationInSeconds } from '@/services/api';
import axios from 'axios';

const MiB = 2 ** 20;

export const validateVideo = (file: File, duration?: number, shouldEnableLongTranslations: boolean = false): boolean => {
    if (!duration) {
        return false;
    }
    const maxDuration = shouldEnableLongTranslations ? NEW_MAX_DURATION_SECONDS : MAX_DURATION_SECONDS;
    if (file.size > MAX_FILE_SIZE_MB * MiB) {
        toast.error({
            title: `File size must be less than ${MAX_FILE_SIZE_MB}MB`,
            toastId: `upload-translation-video-error-size`,
        });
        return false;
    } else if (duration < MIN_DURATION_SECONDS || duration > maxDuration) {
        toast.error({
            title: `Videos should be between ${MIN_DURATION_SECONDS} seconds and 5 minutes long`,
            toastId: `upload-translation-video-error-duration`,
        });
        return false;
    }
    return true;
};

export interface GetDurationFromVideoProps {
    duration?: number;
    isSupportedFormat: boolean;
}

export const getDurationFromVideoWithFallback = async (
    file: File,
    handleUploadFile: (file: File) => Promise<string>,
    setIsBrowserSupportingFormat: (isSupported: boolean) => void
): Promise<GetDurationFromVideoProps> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);

        video.onloadedmetadata = async function () {
            try {
                setIsBrowserSupportingFormat(true);
                resolve({ duration: video.duration, isSupportedFormat: true });
            } catch (err) {
                reject(err);
            }
        };
        video.onerror = async err => {
            if (video.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                try {
                    setIsBrowserSupportingFormat(false);
                    const res = await getVideoDurationInSeconds({ video: file, handleUploadFile });
                    resolve({ duration: res.duration, isSupportedFormat: false });
                } catch (err) {
                    const isCanceled = axios.isAxiosError(err) && err.name === 'CanceledError';
                    if (isCanceled) {
                        resolve({ isSupportedFormat: false });
                    }
                    reject(err);
                }
            } else {
                reject(err);
            }
        };
    });
};
